/**
=========================================================
* Dashboard Berita Terkini — tampilan minimalis, aksen biru / merah / hijau
=========================================================
*/

import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";

import eoCover from "assets/images/eo_cover.png";
import eoCover2 from "assets/images/eocover2.png";
import eoCover3 from "assets/images/eocover3.png";
import eoCover4 from "assets/images/eocover4.png";
import eo_mini1 from "assets/images/eo_mini1.png";
import eo_hp4 from "assets/images/eo_hp4.png";
import eo_hp3 from "assets/images/eo_hp3.png";
import eo_hp2 from "assets/images/eo_hp2.png";

/** Aksen minimal bergilir: info (biru) → error (merah) → success (hijau) */
function getAccentKey(index) {
  const k = index % 3;
  if (k === 0) return "info";
  if (k === 1) return "error";
  return "success";
}

function BeritaTerkiniDashboard() {
  const theme = useTheme();
  const [berita, setBerita] = useState([]);
  const [loadingBerita, setLoadingBerita] = useState(false);
  const [errorBerita, setErrorBerita] = useState("");
  const [openBeritaModal, setOpenBeritaModal] = useState(false);
  const [selectedBerita, setSelectedBerita] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const coverImagesDesktop = [eoCover, eoCover2, eoCover3, eoCover4];
  const coverImagesMobile = [eo_mini1, eo_hp2, eo_hp3, eo_hp4];
  const coverImages = isMobile ? coverImagesMobile : coverImagesDesktop;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 960);
      setCurrentImageIndex(0);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const imageCount = coverImages.length;
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % imageCount);
    }, 4000);
    return () => clearInterval(interval);
  }, [isMobile]);

  useEffect(() => {
    const fetchBerita = async () => {
      setLoadingBerita(true);
      setErrorBerita("");
      try {
        const res = await axios.get(
          "https://smartservice.rotendaokab.go.id/api/beritaPortal"
        );
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setBerita(list);
      } catch (e) {
        setErrorBerita("Gagal memuat berita.");
        setBerita([]);
      } finally {
        setLoadingBerita(false);
      }
    };
    fetchBerita();
  }, []);

  const handleOpenBerita = useCallback((item) => {
    setSelectedBerita(item);
    setOpenBeritaModal(true);
  }, []);

  const handleCloseBerita = () => {
    setOpenBeritaModal(false);
    setSelectedBerita(null);
  };

  const beritaCards = useMemo(() => {
    if (!berita || berita.length === 0) return null;
    return berita.slice(0, 20).map((item, idx) => {
      const images = (item.gambar || "")
        .split(",")
        .map((g) => g.trim())
        .filter(Boolean);
      const mainImage = images[0] || undefined;
      const plainText = String(item.isi || "")
        .replace(/\r?\n/g, " ")
        .trim();
      const shortDesc =
        plainText.length > 140 ? `${plainText.slice(0, 140)}…` : plainText;

      const accent = getAccentKey(idx);
      const borderLeftColor =
        accent === "info"
          ? theme.palette.info.main
          : accent === "error"
          ? theme.palette.error.main
          : theme.palette.success.main;

      return (
        <Grid item xs={12} sm={6} lg={4} key={item.id}>
          <Card
            elevation={0}
            onClick={() => handleOpenBerita(item)}
            sx={{
              height: "100%",
              cursor: "pointer",
              border: "1px solid",
              borderColor: "grey.200",
              borderRadius: 2,
              borderLeftWidth: 4,
              borderLeftColor,
              bgcolor: "background.paper",
              transition: "box-shadow 0.2s ease, border-color 0.2s ease",
              "&:hover": {
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                borderColor: "grey.300",
              },
            }}
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              {mainImage ? (
                <MDBox
                  component="img"
                  src={mainImage}
                  alt=""
                  sx={{
                    width: "100%",
                    height: 128,
                    objectFit: "cover",
                    borderRadius: 1,
                    mb: 1.25,
                    border: "1px solid",
                    borderColor: "grey.100",
                  }}
                />
              ) : (
                <MDBox
                  sx={{
                    height: 72,
                    mb: 1.25,
                    borderRadius: 1,
                    bgcolor: "grey.50",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px dashed",
                    borderColor: "grey.200",
                  }}
                >
                  <Icon sx={{ fontSize: 28, color: "grey.400" }}>article</Icon>
                </MDBox>
              )}
              <MDTypography
                variant="subtitle2"
                fontWeight="600"
                sx={{
                  mb: 0.75,
                  lineHeight: 1.4,
                  color: "text.primary",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {item.judul}
              </MDTypography>
              <MDTypography
                variant="caption"
                color="text.secondary"
                sx={{
                  mb: 1.25,
                  lineHeight: 1.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {shortDesc}
              </MDTypography>
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={0.75}
              >
                {item.kategori ? (
                  <Chip
                    label={item.kategori}
                    size="small"
                    variant="outlined"
                    color={accent}
                    sx={{
                      height: 22,
                      fontSize: "0.7rem",
                      fontWeight: 500,
                      borderWidth: 1,
                      bgcolor:
                        accent === "info"
                          ? "rgba(25, 118, 210, 0.06)"
                          : accent === "error"
                          ? "rgba(211, 47, 47, 0.06)"
                          : "rgba(46, 125, 50, 0.06)",
                    }}
                  />
                ) : (
                  <span />
                )}
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem" }}
                >
                  {item.tanggal || ""}
                </MDTypography>
              </MDBox>
            </CardContent>
          </Card>
        </Grid>
      );
    });
  }, [berita, handleOpenBerita, theme.palette]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pb={3}>
        {/* Banner — tipis, bingkai minimal */}
        <MDBox
          mb={3}
          sx={{
            position: "relative",
            width: "100%",
            height: { xs: 120, md: 152 },
            overflow: "hidden",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "grey.200",
            boxShadow: "none",
          }}
        >
          <MDBox
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: 4,
              zIndex: 2,
              background: `linear-gradient(90deg, ${theme.palette.info.main} 0%, ${theme.palette.success.main} 50%, ${theme.palette.error.main} 100%)`,
              opacity: 0.85,
            }}
          />
          {coverImages.map((image, index) => (
            <MDBox
              key={index}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                opacity: index === currentImageIndex ? 1 : 0,
                transition: "opacity 1s ease-in-out",
              }}
            >
              <img
                src={image}
                alt={`Banner ${index + 1}`}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </MDBox>
          ))}
        </MDBox>

        <MDBox
          sx={{
            pl: 1.5,
            borderLeft: "4px solid",
            borderColor: "info.main",
            mb: 2,
          }}
        >
          <MDTypography variant="h6" fontWeight="600" color="text">
            Informasi Terkini
          </MDTypography>
          <MDTypography variant="caption" color="text.secondary" component="div">
            Sumber: DKISP Rote Ndao — Portal Berita (
            <MDBox
              component="a"
              href="https://smartservice.rotendaokab.go.id/api/beritaPortal"
              target="_blank"
              rel="noreferrer"
              sx={{ color: "info.main", textDecoration: "none", fontWeight: 500 }}
            >
              tautan API
            </MDBox>
            )
          </MDTypography>
        </MDBox>

        <Grid container spacing={2}>
          {loadingBerita && (
            <Grid item xs={12}>
              <MDBox display="flex" justifyContent="center" alignItems="center" py={5} gap={1.5}>
                <CircularProgress size={28} color="info" thickness={4} />
                <MDTypography variant="caption" color="text.secondary">
                  Memuat berita…
                </MDTypography>
              </MDBox>
            </Grid>
          )}
          {errorBerita && !loadingBerita && (
            <Grid item xs={12}>
              <Card
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "error.light",
                  borderLeftWidth: 4,
                  borderLeftColor: "error.main",
                  bgcolor: "rgba(211, 47, 47, 0.04)",
                }}
              >
                <CardContent sx={{ py: 2 }}>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Icon color="error" sx={{ fontSize: 22 }}>
                      error_outline
                    </Icon>
                    <MDTypography variant="body2" color="error" fontWeight="500">
                      {errorBerita}
                    </MDTypography>
                  </MDBox>
                </CardContent>
              </Card>
            </Grid>
          )}
          {!loadingBerita && !errorBerita && beritaCards}
          {!loadingBerita &&
            !errorBerita &&
            (!berita || berita.length === 0) && (
              <Grid item xs={12}>
                <MDBox
                  textAlign="center"
                  py={5}
                  sx={{
                    border: "1px dashed",
                    borderColor: "grey.300",
                    borderRadius: 2,
                    bgcolor: "grey.50",
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: 40,
                      color: "success.main",
                      opacity: 0.5,
                      mb: 1,
                    }}
                  >
                    article
                  </Icon>
                  <MDTypography variant="body2" color="text.secondary">
                    Belum ada berita untuk ditampilkan
                  </MDTypography>
                </MDBox>
              </Grid>
            )}
        </Grid>

        <Dialog
          open={openBeritaModal}
          onClose={handleCloseBerita}
          maxWidth="md"
          fullWidth
          PaperProps={{
            elevation: 0,
            sx: {
              borderRadius: 2,
              border: "1px solid",
              borderColor: "grey.200",
            },
          }}
        >
          <DialogTitle
            sx={{
              borderBottom: "1px solid",
              borderColor: "grey.200",
              py: 2,
            }}
          >
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              gap={2}
            >
              <MDTypography variant="subtitle1" fontWeight="600" sx={{ pr: 1 }}>
                {selectedBerita?.judul || "Detail Berita"}
              </MDTypography>
              <Button
                onClick={handleCloseBerita}
                variant="outlined"
                color="error"
                size="small"
                sx={{ textTransform: "none", minWidth: 72, flexShrink: 0 }}
              >
                Tutup
              </Button>
            </MDBox>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {selectedBerita && (
              <MDBox>
                <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                  {selectedBerita.kategori ? (
                    <Chip
                      label={selectedBerita.kategori}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ fontSize: "0.75rem" }}
                    />
                  ) : null}
                  <MDTypography variant="caption" color="text.secondary">
                    {selectedBerita.tanggal || ""}
                  </MDTypography>
                </MDBox>
                <Divider sx={{ my: 2, borderColor: "grey.200" }} />
                <Grid container spacing={2}>
                  {(selectedBerita.gambar || "")
                    .split(",")
                    .map((g) => g.trim())
                    .filter(Boolean)
                    .slice(0, 3)
                    .map((img, idx) => (
                      <Grid
                        item
                        xs={12}
                        md={idx === 0 ? 12 : 6}
                        key={`${selectedBerita.id}-${idx}`}
                      >
                        <MDBox
                          component="img"
                          src={img}
                          alt={`gambar-${idx}`}
                          sx={{
                            width: "100%",
                            height: idx === 0 ? 260 : 160,
                            objectFit: "cover",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "grey.200",
                          }}
                        />
                      </Grid>
                    ))}
                </Grid>
                <MDBox mt={2}>
                  <MDTypography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
                    {String(selectedBerita.isi || "").trim()}
                  </MDTypography>
                </MDBox>
              </MDBox>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
            <Button
              onClick={handleCloseBerita}
              variant="contained"
              color="info"
              size="small"
              sx={{ textTransform: "none" }}
            >
              Tutup
            </Button>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default BeritaTerkiniDashboard;
